import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getUpcomingMatchById } from '../../services/MatchService';
import { getPlayersByTeamIdLatestFirst } from '../../services/PlayerService';
import PlayerImage from "../../components/PlayerImage";
import TeamVetoComparison from './TeamVetoComparison';
import Loading from '../../components/Loading';
import useLocalStorage from 'use-local-storage';
import { divisionMappings, currentSeason } from '../../Constants';
import { UpcomingMatch } from '../../models/UpcomingMatch';
import { PlayerStats } from '../../models/PlayerStats';

const PreMatchPage = () => {
    const { matchId } = useParams<{ matchId: string }>();
    const [isLoading, setIsLoading] = useState(true);
    const [upcomingMatch, setUpcomingMatch] = useState<UpcomingMatch | null>(null);
    const [team1Players, setTeam1Players] = useState<PlayerStats[]>([]);
    const [team2Players, setTeam2Players] = useState<PlayerStats[]>([]);
    const [isDark] = useLocalStorage<boolean>('isDark', false);

    const fetchMatchData = useCallback(async () => {
        try {
            if (!matchId) throw new Error('No matchId provided');
            const matchData = await getUpcomingMatchById(matchId);
            console.log('Fetched match data:', matchData);
            if (!matchData) throw new Error('No match data found');
            setUpcomingMatch(matchData);
        } catch (error) {
            console.error('Error fetching match data:', error);
        } finally {
            setIsLoading(false);
        }
    }, [matchId]);

    const fetchTeamPlayers = useCallback(async (teamId: string, setPlayers: React.Dispatch<React.SetStateAction<PlayerStats[]>>) => {
        try {
            // Using the latestFirst parameter to get the most recent players
            const players = await getPlayersByTeamIdLatestFirst(teamId, currentSeason, true);
            console.log(`Fetched players for team ${teamId}:`, players);
            setPlayers(players.slice(0, 5)); // Limit to 5 players
        } catch (error) {
            console.error(`Error fetching players for team ${teamId}:`, error);
        }
    }, []);

    useEffect(() => {
        fetchMatchData();
    }, [fetchMatchData]);

    useEffect(() => {
        if (upcomingMatch) {
            console.log('Upcoming match data:', upcomingMatch);
            fetchTeamPlayers(upcomingMatch.team1.teamId, setTeam1Players);
            fetchTeamPlayers(upcomingMatch.team2.teamId, setTeam2Players);
        }
    }, [upcomingMatch, fetchTeamPlayers]);

    if (isLoading) {
        return (
            <div className={`container text-center ${isDark ? 'text-light bg-dark' : ''}`}>
                <Loading />
            </div>
        );
    }

    if (!upcomingMatch) {
        return <div>No match data available.</div>;
    }

    return (
        <div className={`prematch-page ${isDark ? 'bg-dark text-light' : ''}`}>
            <div className="container text-center">
                <div
                    className="row align-items-center"
                    style={{
                        backgroundColor: '#e25623',
                        borderRadius: '10px',
                        padding: '15px 0',
                        marginBottom: '20px',
                    }}
                >
                    <div className="col-4 d-flex align-items-center justify-content-center">
                        <Link
                            to={`/team/${upcomingMatch.team1.teamId}`}
                            className="text-decoration-none d-flex align-items-center text-light"
                        >
                            <img
                                src={upcomingMatch.team1.teamLogo}
                                alt={`${upcomingMatch.team1.teamName} Logo`}
                                style={{ width: 80, marginRight: 10 }}
                            />
                            <h4 className="m-0">{upcomingMatch.team1.teamName}</h4>
                        </Link>
                    </div>
                    <div className="col-4 text-center text-light">
                        <div>
                        {`${divisionMappings.get(upcomingMatch.division)} - Sæson ${currentSeason} - ${new Date(upcomingMatch.match_time).toLocaleDateString('da-DK').replaceAll('.', '/')}`}
                        </div>
                        <h1 className="my-2">
                            <span className="text-white">vs</span>
                        </h1>
                    </div>
                    <div className="col-4 d-flex align-items-center justify-content-center">
                        <Link
                            to={`/team/${upcomingMatch.team2.teamId}`}
                            className="text-decoration-none d-flex align-items-center text-light"
                        >
                            <h4 className="m-0 me-2">{upcomingMatch.team2.teamName}</h4>
                            <img
                                src={upcomingMatch.team2.teamLogo}
                                alt={`${upcomingMatch.team2.teamName} Logo`}
                                style={{ width: 80, marginLeft: 10 }}
                            />
                        </Link>
                    </div>
                </div>
            </div>
            
            {/* Combined Map Comparison and Player Lineups Section */}
            <div className="container px-0 my-4">
                <div className="row mx-0" style={{ minHeight: '620px' }}>
                    {/* Team 1 Players */}
                    <div className="col-md-3 px-2 h-200">
                        <div className={`${isDark ? 'text-light' : ''} h-100 d-flex flex-column justify-content-between`}>
                            <div className="row g-2 flex-grow-1">{/* reduced g-3 to g-2 for less spacing */}
                                {team1Players.map((player, index) => (
                                    <div className="col-12" key={player.steamID || index}>
                                        <Link to={`/player/${player.steamID}`} className="text-decoration-none">
                                            <div
                                                className="card"
                                                style={
                                                    isDark
                                                        ? {
                                                            backgroundColor: index % 2 === 0 ? '#343a40' : '#212529',
                                                            border: '1px solid #111',
                                                        }
                                                        : { backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff' }
                                                }
                                            >
                                                <div className="card-body p-3">{/* reduced p-4 to p-3 */}
                                                    <div className="d-flex align-items-center">
                                                        <PlayerImage id={player.steamID} width={75} />{/* reduced 85 to 75 */}
                                                        <div className="ms-3 text-start">
                                                            <p className={`mb-1 ${isDark ? 'text-light' : 'text-dark'}`} style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                                {player.account ? player.account.playerName : player.steamID}
                                                            </p>
                                                            <p className={`mb-0 ${isDark ? 'text-light' : 'text-dark'}`} style={{ fontSize: '16px' }}>
                                                                Rating: {player.hltv2Rating?.toFixed(2) || 'N/A'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                ))}
                                {/* Add placeholder cards if needed */}
                                {team1Players.length < 5 && 
                                  [...Array(5 - team1Players.length)].map((_, idx) => (
                                    <div className="col-12" key={`empty1-${idx}`}>
                                        <div className="card" style={{ 
                                            visibility: 'hidden',
                                            height: '110px' // Reduced card height
                                        }}>
                                            <div className="card-body"></div>
                                        </div>
                                    </div>
                                  ))
                                }
                            </div>
                        </div>
                    </div>
                    
                    {/* Map Comparison */}
                    <div className="col-md-6 px-2 h-100">
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <TeamVetoComparison 
                                team1Id={upcomingMatch.team1.teamId}
                                team2Id={upcomingMatch.team2.teamId}
                                team1Name={upcomingMatch.team1.teamName}
                                team2Name={upcomingMatch.team2.teamName}
                            />
                        </div>
                    </div>
                    
                    {/* Team 2 Players */}
                    <div className="col-md-3 px-2 h-200">
                        <div className={`${isDark ? 'text-light' : ''} h-100 d-flex flex-column justify-content-between`}>
                            <div className="row g-2 flex-grow-1">{/* reduced g-3 to g-2 for less spacing */}
                                {team2Players.map((player, index) => (
                                    <div className="col-12" key={player.steamID || index}>
                                        <Link to={`/player/${player.steamID}`} className="text-decoration-none">
                                            <div
                                                className="card"
                                                style={
                                                    isDark
                                                        ? {
                                                            backgroundColor: index % 2 === 0 ? '#343a40' : '#212529',
                                                            border: '1px solid #111',
                                                        }
                                                        : { backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff' }
                                                }
                                            >
                                                <div className="card-body p-3">{/* reduced p-4 to p-3 */}
                                                    <div className="d-flex align-items-center">
                                                        <PlayerImage id={player.steamID} width={75} />{/* reduced 85 to 75 */}
                                                        <div className="ms-3 text-start">
                                                            <p className={`mb-1 ${isDark ? 'text-light' : 'text-dark'}`} style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                                {player.account ? player.account.playerName : player.steamID}
                                                            </p>
                                                            <p className={`mb-0 ${isDark ? 'text-light' : 'text-dark'}`} style={{ fontSize: '16px' }}>
                                                                Rating: {player.hltv2Rating?.toFixed(2) || 'N/A'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                ))}
                                {/* Add placeholder cards if needed */}
                                {team2Players.length < 5 && 
                                  [...Array(5 - team2Players.length)].map((_, idx) => (
                                    <div className="col-12" key={`empty2-${idx}`}>
                                        <div className="card" style={{ 
                                            visibility: 'hidden',
                                            height: '110px' // Reduced card height
                                        }}>
                                            <div className="card-body"></div>
                                        </div>
                                    </div>
                                  ))
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PreMatchPage;